import { createApiError, normalizeError } from '@/core/error-handling/ApiErrorHandler';
import { logger } from '@/core/logging/logger';
import { apiClient } from '@/shared/services/api/client';
import { unwrapEnvelope } from '@/shared/services/api/envelope';
import type { ApiEnvelope } from '@/shared/services/api/envelope';
import type { AxiosResponse } from 'axios';
import type { MasterCrud, MasterPage, MasterQuery, PagedContent } from '@/features/masters';
import { env } from '@/app/config/env';
import type {
  TemplateDetail,
  TemplateExportFormat,
  TemplateImportResult,
  TemplateKind,
  TemplateOrgUnit,
  TemplatePayload,
  TemplateRecord,
} from '../models/template.types';
import { scopeOfQuery, scopeQuery } from '../models/hierarchyScope';
import { mockTemplateApi } from '../__mocks__/templates.mock';

/**
 * The only module that knows the Templates endpoints.
 *
 * One client per kind, because `kind` is required on the listing -- three
 * screens are three lists -- but one implementation, because that is the only
 * thing that differs.
 */

const BASE = '/templates';
const FALLBACK = 'Unable to load templates. Please try again.';

/* A refused screen is the page's news to report, not grounds to throw the user
   out of the shell. Same reasoning as the masters' own clients. */
const allowForbidden = { _allowForbidden: true } as const;

/** The filter builder's conditions, as the endpoint reads them. */
function filterParams(query: MasterQuery): string[] {
  return (query.filters ?? [])
    .filter((condition) => condition.column !== '' && condition.value.trim() !== '')
    .map((condition) => `${condition.column}:${condition.comparison}:${condition.value.trim()}`);
}

function listParams(kind: TemplateKind, query: MasterQuery): Record<string, string | number | string[]> {
  const params: Record<string, string | number | string[]> = {
    kind,
    page: query.page,
    size: query.size,
  };

  const search = query.search?.trim();
  if (search) params.search = search;

  /* '' is the screen's "All Status", which the server reads as Active, Inactive
     and Draft -- and never the deleted. */
  const status = query.status?.trim();
  if (status) params.status = status;

  const filters = filterParams(query);
  if (filters.length > 0) params.filter = filters;

  if (query.sortField && query.sortOrder) {
    params.sort = `${query.sortField},${query.sortOrder === 1 ? 'asc' : 'desc'}`;
  }

  /* A hierarchy-mapped kind's list filter, carried as the query's extras: `legalEntityIds=1,2`. */
  Object.assign(params, scopeQuery(scopeOfQuery(query)));

  return params;
}

const one = (id: number) => `${BASE}/${String(id)}`;

/**
 * A templates listing response, as a page of rows.
 *
 * Shared with every other endpoint that answers with template rows -- the
 * active listing behind New Purchase Request is the second -- so
 * the envelope and the paging are read one way.
 */
export function mapTemplateListing(
  response: AxiosResponse<ApiEnvelope<PagedContent<TemplateRecord> | TemplateRecord[]>>,
  fallback: string = FALLBACK,
): MasterPage<TemplateRecord> {
  const data = unwrapEnvelope(response, fallback);
  const rows = Array.isArray(data) ? data : (data.content ?? []);
  return {
    rows,
    total: Array.isArray(data) ? rows.length : (data.totalElements ?? rows.length),
  };
}

/** The five calls `useMasterTable` drives, scoped to one kind. */
export function createTemplateApi(kind: TemplateKind): MasterCrud<TemplateRecord, TemplatePayload> {
  if (env.ENABLE_MOCKS) return mockTemplateApi(kind);

  return {
    list: async (query): Promise<MasterPage<TemplateRecord>> => {
      try {
        const response = await apiClient.get<ApiEnvelope<PagedContent<TemplateRecord> | TemplateRecord[]>>(
          BASE,
          { ...allowForbidden, params: listParams(kind, query) },
        );
        return mapTemplateListing(response);
      } catch (error) {
        throw asReadableListingError(error);
      }
    },

    get: async (id) =>
      unwrapEnvelope(await apiClient.get<ApiEnvelope<TemplateDetail>>(one(id), allowForbidden), FALLBACK),

    create: async (payload) =>
      unwrapEnvelope(await apiClient.post<ApiEnvelope<TemplateDetail>>(BASE, payload), FALLBACK),

    update: async (id, payload) =>
      unwrapEnvelope(await apiClient.put<ApiEnvelope<TemplateDetail>>(one(id), payload), FALLBACK),

    // 204 with no body, so there is nothing to unwrap.
    remove: async (id) => {
      await apiClient.delete(one(id));
    },
  };
}

/**
 * Keeps an API-contract complaint off the screen, without losing it.
 *
 * A **400 on the listing read** is always the screen asking for something this
 * API does not serve -- an undeclared sort or filter property, or a malformed
 * condition. The server says so precisely, and precisely in its own terms:
 *
 *     Unsupported sort property: statusName.
 *     Allowed: [createdBy, modifiedDate, fieldCount, templateCategory, name, createdDate]
 *
 * That is a sentence for whoever is fixing the mismatch, and there is nothing in
 * it a user can act on -- they clicked a column header. So the panel gets a plain
 * sentence and the original goes to the console, which is where the person who
 * needs it is already looking, alongside the failing request in the network tab.
 *
 * **Only 400.** A 403 means the permission is missing and a 500 means the server
 * broke; both are the user's news and are passed through unchanged. Nothing here
 * swallows an error -- it is re-thrown either way, and the listing still fails.
 */
function asReadableListingError(error: unknown): unknown {
  const normalised = normalizeError(error);
  if (normalised.status !== 400) {
    return error;
  }

  logger.warn('The templates listing was refused by the API', normalised.message);

  return createApiError({
    code: normalised.code,
    status: normalised.status,
    message:
      'This list could not be loaded with the current sort and filters. ' +
      'Clear them, or try again.',
  });
}

/**
 * The full record behind a list row -- every section and field.
 *
 * Separate from `crud.get` only in its return type: the builder needs the tree,
 * and a listing row does not carry it. An Edit seeded from the row alone would
 * silently discard the field configuration on the next save.
 */
export async function getTemplateDetail(id: number): Promise<TemplateDetail> {
  if (env.ENABLE_MOCKS) return mockTemplateApi('RFQ').detail(id);
  return unwrapEnvelope(
    await apiClient.get<ApiEnvelope<TemplateDetail>>(one(id), allowForbidden),
    FALLBACK,
  );
}

/* -- the builder's lookups: Template Category and the Business Unit / Division / Department cascade -------- */

const ACTIVE_PAGE = { status: 'Active', page: 0, size: 200 };

interface NamedRow {
  id: number;
  name?: string | null;
}

async function namedRows(url: string, params: Record<string, string | number>): Promise<NamedRow[]> {
  const page = unwrapEnvelope(
    await apiClient.get<ApiEnvelope<PagedContent<NamedRow>>>(url, { params: { ...ACTIVE_PAGE, ...params } }),
    FALLBACK,
  );
  return page.content;
}

/** Template Category's options: the Category master's Active names (`GET /product-categories`), by name. */
export async function getTemplateCategories(): Promise<string[]> {
  if (env.ENABLE_MOCKS) return mockTemplateApi('RFQ').categories();
  const rows = await namedRows('/product-categories', {});
  return rows
    .map((row) => row.name?.trim() ?? '')
    .filter((name) => name !== '')
    .sort((a, b) => a.localeCompare(b));
}

function asUnits(rows: NamedRow[]): TemplateOrgUnit[] {
  return rows
    .filter((row) => row.name)
    .map((row) => ({ id: row.id, name: (row.name ?? '').trim() }))
    .sort((a, b) => a.name.localeCompare(b.name));
}

/** The selected entity's Active Business Units. */
export async function getBusinessUnits(legalEntityId: number | null): Promise<TemplateOrgUnit[]> {
  if (env.ENABLE_MOCKS) return mockTemplateApi('RFQ').businessUnits();
  return asUnits(await namedRows('/business-units', legalEntityId === null ? {} : { legalEntityId }));
}

/** A Business Unit's Active Divisions. */
export async function getDivisions(businessUnitId: number): Promise<TemplateOrgUnit[]> {
  if (env.ENABLE_MOCKS) return mockTemplateApi('RFQ').divisions(businessUnitId);
  return asUnits(await namedRows('/divisions', { businessUnitId }));
}

/** A Division's Active Departments. */
export async function getDepartments(divisionId: number): Promise<TemplateOrgUnit[]> {
  if (env.ENABLE_MOCKS) return mockTemplateApi('RFQ').departments(divisionId);
  return asUnits(await namedRows('/departments', { divisionId }));
}

/**
 * Activate or deactivate one template.
 *
 * Its own call because the row menu's control has no form behind it -- sending a
 * whole template to change one column would mean inventing values for every
 * field it is not changing, the section tree included.
 */
export async function setTemplateStatus(id: number, status: string): Promise<TemplateDetail> {
  if (env.ENABLE_MOCKS) return mockTemplateApi('RFQ').setStatus(id, status);
  return unwrapEnvelope(
    await apiClient.put<ApiEnvelope<TemplateDetail>>(`${one(id)}/status`, { status }),
    FALLBACK,
  );
}

/**
 * The export, in the format the dialog's card chose.
 *
 * All four formats the reference draws are served -- Excel, CSV, PDF and JSON.
 * None needs a package: the server writes the workbook and the PDF itself, for
 * the same reason this rule exists on the client.
 *
 * **The current view travels with the request** whatever the format: the search,
 * the status, the filter conditions and the visible columns. What you export is
 * what you are looking at.
 */
export async function exportTemplates(
  kind: TemplateKind,
  query: MasterQuery,
  format: TemplateExportFormat,
  columns: readonly string[],
): Promise<Blob> {
  const params: Record<string, string | string[]> = { kind, format, scope: 'view' };

  const search = query.search?.trim();
  if (search) params.search = search;

  const status = query.status?.trim();
  if (status) params.status = status;

  const filters = filterParams(query);
  if (filters.length > 0) params.filter = filters;

  /* Only the printed table has columns to narrow -- the other three carry the
     whole definition, whose shape the visible columns say nothing about. */
  if (format === 'pdf' && columns.length > 0) params.columns = columns.join(',');

  const response = await apiClient.get<Blob>(`${BASE}/export`, { params, responseType: 'blob' });
  return response.data;
}

/**
 * The import template -- `GET /templates/import-template?kind=&format=` (2026-10-19): an empty file headed by the
 * listing's columns, one template per row once filled. Built here when mocks are on, from the kind's own columns.
 */
export async function downloadTemplateImportTemplate(
  kind: TemplateKind,
  format: 'xlsx' | 'csv',
  columns: readonly string[],
): Promise<Blob> {
  if (env.ENABLE_MOCKS) return new Blob([`${columns.join(',')}\n`], { type: 'text/csv' });
  const response = await apiClient.get<Blob>(`${BASE}/import-template`, {
    params: { kind, format },
    responseType: 'blob',
  });
  return response.data;
}

/**
 * The import: the file's own bytes as the body, and back what landed and what did not.
 *
 * **Bytes, not text.** A workbook is binary, and reading it with `file.text()`
 * decodes it as UTF-8 and destroys it before it is ever sent -- which is what
 * made an Excel import fail. An `ArrayBuffer` is the file exactly as it sits on
 * disk, and the server decides from its first two bytes whether it is unzipping
 * a workbook or reading a CSV.
 *
 * The body is raw rather than multipart, as the masters' import is: multipart
 * would add a boundary, a part name and a server-side parser for no gain.
 */
export async function importTemplates(
  kind: TemplateKind,
  file: File,
): Promise<TemplateImportResult> {
  const body = await file.arrayBuffer();

  return unwrapEnvelope(
    await apiClient.post<ApiEnvelope<TemplateImportResult>>(`${BASE}/import`, body, {
      params: { kind },
      /* The file's own media type, so what reaches the server is labelled the way
         the person's disk labels it. The endpoint declares no `consumes` and
         reads the bytes, so the label is information rather than a gate -- the
         gate is what made a mismatch come back as "Method POST is not
         supported". */
      headers: { 'Content-Type': file.type || 'application/octet-stream' },
    }),
    FALLBACK,
  );
}
